import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MANAGER_ROLES } from "../lib/constants";
import { LostReasonModal } from "../components/LostReasonModal";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const dayKey = (iso) => {
  if (!iso) return "Unknown";
  const d = new Date(iso);
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const dayLabel = (key) => {
  const today = dayKey(new Date().toISOString());
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (key === today) return "Today";
  if (key === dayKey(y.toISOString())) return "Yesterday";
  return key;
};

export default function Kanban({ expiredOnly = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [consultant, setConsultant] = useState("");
  const [lostFor, setLostFor] = useState(null);
  const canFilter = MANAGER_ROLES.includes(user?.role);

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => {
    load();
    api.get("/config/stages").then((r) => setStages(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const visible = useMemo(() => leads.filter((l) => !consultant || l.assigned_to === consultant), [leads, consultant]);

  const applyStage = async (leadId, stageId, extra = {}) => {
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage: stageId, ...extra } : l)));
    try { await api.put(`/leads/${leadId}`, { stage: stageId, ...extra }); toast.success("Stage updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); load(); }
  };

  const drop = (stageId) => {
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    if (lead && lead.stage !== stageId) {
      if (stageId === "lost") setLostFor(lead);
      else applyStage(dragId, stageId);
    }
    setDragId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-head font-extrabold tracking-tight">{expiredOnly ? "Quote expired" : "Pipeline"}</h1>
          <p className="text-muted-foreground">Drag leads between stages · grouped by the day they were received</p>
        </div>
        {canFilter && (
          <div>
            <label className="label-caps">Consultant</label>
            <select value={consultant} onChange={(e) => setConsultant(e.target.value)} data-testid="kanban-consultant-filter"
              className="block mt-1 px-3 py-2 rounded-md border border-input bg-card text-sm min-w-[200px]">
              <option value="">All consultants</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.filter((s) => !expiredOnly || s.id === "quote_expired").map((st) => {
          const items = visible.filter((l) => l.stage === st.id).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
          const groups = [];
          for (const l of items) {
            const k = dayKey(l.created_at);
            const g = groups.find((x) => x.key === k);
            if (g) g.items.push(l); else groups.push({ key: k, items: [l] });
          }
          return (
            <div key={st.id} data-testid={`kanban-column-${st.id}`}
              onDragOver={(e) => e.preventDefault()} onDrop={() => drop(st.id)}
              className="w-72 flex-shrink-0 bg-muted/30 rounded-md p-3">
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="font-head font-semibold text-sm">{st.name}</span>
                <span className="text-xs bg-card border border-border rounded-full px-2 py-0.5">{items.length}</span>
              </div>
              <div className="space-y-3">
                {groups.map((g) => (
                  <div key={g.key} data-testid={`kanban-group-${st.id}-${g.key}`}>
                    <div className="flex items-center gap-2 px-1 mb-1.5">
                      <Icons.CalendarDays size={12} className="text-muted-foreground" />
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{dayLabel(g.key)}</span>
                      <span className="text-[11px] text-muted-foreground">— {g.items.length} lead{g.items.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="space-y-2">
                      {g.items.map((l) => (
                        <div key={l.id} draggable onDragStart={() => setDragId(l.id)}
                          onClick={() => navigate(`/app/leads/${l.id}`)}
                          data-testid={`kanban-card-${l.uid}`}
                          className="bg-card border border-border border-l-4 border-l-primary rounded-md p-3 cursor-pointer hover:shadow-sm transition-all">
                          <div className="text-xs font-semibold text-primary flex items-center gap-1">
                            {l.is_duplicate && <span data-testid={`kanban-dup-${l.uid}`} title="Possible duplicate lead" className="inline-block w-2 h-2 rounded-full bg-red-500 align-middle animate-pulse" />}
                            {l.uid}{l.locked && <Icons.Lock size={11} className="text-muted-foreground ml-auto" />}
                          </div>
                          <div className="font-medium text-sm">{l.first_name} {l.last_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{l.suburb || l.full_address}</div>
                          {canFilter && !consultant && l.assigned_to && <div className="text-[10px] text-muted-foreground mt-1">{users.find((u) => u.id === l.assigned_to)?.name}</div>}
                          {l.substage && <div className="text-[10px] mt-1 bg-secondary/10 text-secondary rounded px-1.5 py-0.5 inline-block">{l.substage}</div>}
                          {l.stage === "lost" && l.lost_reason && <div className="text-[10px] mt-1 bg-red-50 text-red-700 rounded px-1.5 py-0.5 inline-block">{l.lost_reason}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {lostFor && <LostReasonModal lead={lostFor} onClose={() => setLostFor(null)}
        onConfirm={(reason, notes) => { applyStage(lostFor.id, "lost", { lost_reason: reason, lost_notes: notes }); setLostFor(null); }} />}
    </div>
  );
}
